import type { JSX } from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { FakeLink } from '@change-corgi/design-system-legacy/components/actions';
import { InlineMessage } from '@change-corgi/design-system-legacy/components/alerts';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { Heading, Text } from '@change-corgi/design-system-legacy/typography';

import { useEmailVerificationModalBody } from './hooks/useEmailVerificationModalBody';

type Props = {
	path: string;
	onContinueAsGuest?: () => void;
	userId: string;
	isSunriseEnabled: boolean;
};

export function EmailVerificationModalBody({ path, onContinueAsGuest, userId, isSunriseEnabled }: Props): JSX.Element {
	const {
		data: { resendEmailResult },
		actions: { resendVerificationEmail },
	} = useEmailVerificationModalBody(path, userId);

	return (
		<>
			{resendEmailResult === 'success' && (
				<InlineMessage variant="success" role="alert" p={8} mb={64}>
					<Translate value="fe.pages.sponsors.verify_email.verify_email_resent" />
				</InlineMessage>
			)}
			{resendEmailResult === 'error' && (
				<InlineMessage variant="error" role="alert" p={8} mb={64}>
					<Translate value="fe.errors.sponsors.send_verify_email" />
				</InlineMessage>
			)}
			<Heading
				as="h1"
				size={isSunriseEnabled ? 'h2' : ['subtitle', 'h3']}
				sx={{
					textAlign: 'center',
					...(isSunriseEnabled && {
						color: 'sunrise.semantic.typography.strong',
					}),
				}}
			>
				<Translate value="fe.pages.sponsors.verify_email.verify_email" />
			</Heading>
			<Text
				as="div"
				sx={{
					textAlign: 'center',
					...(isSunriseEnabled && {
						color: 'sunrise.semantic.typography.strong',
					}),
				}}
				mt={16}
			>
				<Translate value="fe.pages.sponsors.verify_email.require_verify" />
			</Text>
			<Box
				backgroundColor={isSunriseEnabled ? 'sunrise.semantic.fill.weaker' : 'primary-greyBackground'}
				my={24}
				p={8}
				sx={{
					...(isSunriseEnabled && {
						p: 10,
						borderRadius: 8,
						mb: 16,
					}),
				}}
			>
				<Text
					as="div"
					sx={{
						textAlign: 'center',
						...(isSunriseEnabled && {
							color: 'sunrise.semantic.typography.strong',
						}),
					}}
				>
					<Translate value="fe.pages.sponsors.verify_email.check_verify" />{' '}
				</Text>
				<FakeLink
					onClick={resendVerificationEmail}
					sx={{
						width: '100%',
						textAlign: 'center',
						...(isSunriseEnabled && {
							color: 'sunrise.semantic.typography.strong',
							width: '100%',
							textAlign: 'center',
							py: 12,
						}),
					}}
				>
					<Translate value="fe.pages.sponsors.verify_email.resend_email" />
				</FakeLink>
			</Box>
			{onContinueAsGuest && (
				<Text as="div" sx={{ textAlign: 'center' }} mt={40} mb={24}>
					<FakeLink onClick={onContinueAsGuest}>
						<Translate value="fe.pages.sponsors.guest_login" />
					</FakeLink>
				</Text>
			)}
		</>
	);
}
